import { lazy, Suspense, useCallback, useEffect, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { api } from './api';
import { Layout } from './components/Layout';
import { Loading } from './components/Ui';
import { defaultSchoolSettings, SchoolSettingsProvider } from './components/SchoolSettings';
import type { SchoolSettings, SystemModule, User } from './types';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { StudentsPage } from './pages/StudentsPage';
import { FeesPage } from './pages/FeesPage';
import { FinancePage } from './pages/FinancePage';
import { PartnersPage } from './pages/PartnersPage';
import { PayrollPage } from './pages/PayrollPage';
import { AssetsPage } from './pages/AssetsPage';
import { CampusesPage } from './pages/CampusesPage';
import { ReportsPage } from './pages/ReportsPage';
import { AdminPage } from './pages/AdminPage';
import { BooksPage } from './pages/BooksPage';
import { StudentFormPage } from './pages/StudentFormPage';
import { TransportPage } from './pages/TransportPage';
import { TuitionPage } from './pages/TuitionPage';
import { AcademicsPage } from './pages/AcademicsPage';
import { AdmissionsPage } from './pages/AdmissionsPage';
import { ModulesPage } from './pages/ModulesPage';
import { SchoolSettingsPage } from './pages/SchoolSettingsPage';
import { CommunicationsPage } from './pages/CommunicationsPage';
import { InventoryPage } from './pages/InventoryPage';
import { DataCenterPage } from './pages/DataCenterPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { AttendancePage } from './pages/AttendancePage';
const AssessmentsPage=lazy(()=>import('./pages/AssessmentsPage').then(module=>({default:module.AssessmentsPage})));
const AutomationCenterPage=lazy(()=>import('./pages/AutomationCenterPage').then(module=>({default:module.AutomationCenterPage})));
const IntelligencePage=lazy(()=>import('./pages/IntelligencePage').then(module=>({default:module.IntelligencePage})));
const PortalPage=lazy(()=>import('./pages/PortalPage').then(module=>({default:module.PortalPage})));

export default function App() {
  const isPortal=window.location.pathname.startsWith('/portal');
  const [user,setUser] = useState<User | null | undefined>(undefined);
  const [modules,setModules] = useState<SystemModule[] | undefined>(undefined);
  const [settings,setSettings] = useState<SchoolSettings | undefined>(undefined);
  useEffect(() => { if(isPortal){setUser(null);return;}api<{user:User}>('/auth/me').then(result=>setUser(result.user)).catch(()=>setUser(null)); },[isPortal]);
  const loadSettings=useCallback(async()=>{try{const result=await api<{settings:SchoolSettings}>('/settings');setSettings(result.settings||defaultSchoolSettings);}catch{setSettings(defaultSchoolSettings);}},[]);
  useEffect(()=>{void loadSettings();},[loadSettings]);
  const loadModules=useCallback(async()=>{if(!user)return;const result=await api<{modules:SystemModule[]}>('/modules');setModules(result.modules);},[user]);
  useEffect(()=>{void loadModules();},[loadModules]);
  if (settings === undefined || (!isPortal&&user===undefined)) return <div className="app-loading"><Loading label="Opening secure workspace..."/></div>;
  if (isPortal) return <SchoolSettingsProvider settings={settings} reload={loadSettings}><Suspense fallback={<div className="app-loading"><Loading label="Opening family portal..."/></div>}><PortalPage/></Suspense></SchoolSettingsProvider>;
  if (!user) return <SchoolSettingsProvider settings={settings} reload={loadSettings}><LoginPage onLogin={setUser}/></SchoolSettingsProvider>;
  if (!modules) return <div className="app-loading"><Loading label="Loading enabled modules..."/></div>;
  const gate=(key:string,node:ReactNode)=><ModuleGate modules={modules} moduleKey={key}>{node}</ModuleGate>;
  return <SchoolSettingsProvider settings={settings} reload={loadSettings}><Suspense fallback={<Loading label="Opening workspace..."/>}><Layout user={user} modules={modules} onLogout={()=>{setModules(undefined);setUser(null);}}>
    <Routes>
      <Route path="/" element={<DashboardPage user={user}/>}/>
      <Route path="/students" element={gate('students',<StudentsPage user={user}/>)}/>
      <Route path="/students/new" element={gate('students',<StudentFormPage user={user}/>)}/>
      <Route path="/students/:id/edit" element={gate('students',<StudentFormPage user={user}/>)}/>
      <Route path="/academics" element={gate('academics',<AcademicsPage user={user}/>)}/>
      <Route path="/attendance" element={gate('attendance',<AttendancePage user={user}/>)}/>
      <Route path="/assessments" element={gate('assessments',<AssessmentsPage user={user}/>)}/>
      <Route path="/admissions" element={gate('admissions',<AdmissionsPage user={user}/>)}/>
      <Route path="/fees" element={gate('fees',<FeesPage user={user}/>)}/>
      <Route path="/transport" element={gate('transport',<TransportPage user={user}/>)}/>
      <Route path="/tuition" element={gate('tuition',<TuitionPage user={user}/>)}/>
      <Route path="/books" element={gate('books',<BooksPage user={user}/>)}/>
      <Route path="/communications" element={gate('communications',<CommunicationsPage user={user}/>)}/>
      <Route path="/inventory" element={gate('inventory',<InventoryPage user={user}/>)}/>
      <Route path="/finance" element={gate('finance',<FinancePage user={user}/>)}/>
      <Route path="/partners" element={gate('partners',<PartnersPage user={user}/>)}/>
      <Route path="/payroll" element={gate('payroll',<PayrollPage user={user}/>)}/>
      <Route path="/assets" element={gate('assets',<AssetsPage user={user}/>)}/>
      <Route path="/campuses" element={gate('campuses',<CampusesPage user={user}/>)}/>
      <Route path="/reports" element={gate('reports',<ReportsPage user={user}/>)}/>
      <Route path="/automation-center" element={gate('automation_center',<AutomationCenterPage user={user}/>)}/>
      <Route path="/intelligence" element={gate('intelligence',<IntelligencePage user={user}/>)}/>
      <Route path="/admin" element={user.role === 'administrator' ? <AdminPage/> : <Navigate to="/" replace/>}/>
      <Route path="/modules" element={user.role === 'administrator' ? <ModulesPage modules={modules} onRefresh={loadModules}/> : <Navigate to="/" replace/>}/>
      <Route path="/school-settings" element={user.role === 'administrator' ? <SchoolSettingsPage/> : <Navigate to="/" replace/>}/>
      <Route path="/data-center" element={user.role === 'administrator' ? <DataCenterPage/> : <Navigate to="/" replace/>}/>
      <Route path="/integrations" element={user.role === 'administrator' ? <IntegrationsPage/> : <Navigate to="/" replace/>}/>
      <Route path="/onboarding" element={user.role === 'administrator' ? <OnboardingPage/> : <Navigate to="/" replace/>}/>
      <Route path="*" element={<Navigate to="/" replace/>}/>
    </Routes>
  </Layout></Suspense></SchoolSettingsProvider>;
}

function ModuleGate({modules,moduleKey,children}:{modules:SystemModule[];moduleKey:string;children:ReactNode}) {
  const module=modules.find(item=>item.module_key===moduleKey);
  if(module&&!module.active)return <Navigate to="/" replace/>;
  return children;
}
